/**
 * A password strength estimate that looks for the patterns attackers try first.
 *
 * Two numbers come out, on purpose. The **character-set entropy** is what most meters
 * show: length × log₂(size of the character pool), the strength of the password *if
 * every character had been picked at random*. The **pattern-aware estimate** asks how
 * many guesses a cracker working through likely passwords would need: it finds every
 * common password, dictionary word, name, l33t spelling, sequence, repeat, keyboard
 * walk, year and date inside the password, prices each one, prices letters that read
 * like English by a letter-pair model, scores any leftover characters as random, and
 * keeps the cheapest way of covering the whole password.
 *
 * The method follows Dropbox's zxcvbn (Wheeler, USENIX Security 2016), simplified and
 * with much shorter word lists — so it can only *under*-estimate how quickly a password
 * that is built from patterns falls, never vouch that one is safe. The lists live in
 * `password-lists.ts` and are passed in, so a page can load them after it renders.
 * Everything runs on the reader's device; nothing is sent or stored.
 */

/* ----------------------------------------------------------------- attacks */

export type AttackId =
  | "onlineThrottled"
  | "onlineUnthrottled"
  | "offlineSlow"
  | "offlineFast";

/**
 * Guessing speeds, slowest first. Throttled online guessing is a site that slows or
 * locks a login after failures (100 an hour); unthrottled is 10 a second. Offline
 * figures are for a stolen password database: 10⁴ a second against a deliberately slow
 * hash (bcrypt, scrypt, Argon2), 10¹¹ against an unsalted fast hash such as MD5 — about
 * what one current high-end graphics card manages.
 */
export const ATTACKS: readonly { id: AttackId; perSecond: number }[] = [
  { id: "onlineThrottled", perSecond: 100 / 3600 },
  { id: "onlineUnthrottled", perSecond: 10 },
  { id: "offlineSlow", perSecond: 1e4 },
  { id: "offlineFast", perSecond: 1e11 },
];

/** A Julian year, in seconds. */
export const SECONDS_PER_YEAR = 31_557_600;

/* ------------------------------------------------------------ dictionaries */

/** The ranked lists a dictionary match looks words up in, most common first. */
export interface PasswordLists {
  passwords: readonly string[];
  english: readonly string[];
  firstNames: readonly string[];
  surnames: readonly string[];
}

export type DictionaryName = "passwords" | "english" | "names" | "surnames";

interface Dictionary {
  name: DictionaryName;
  ranks: ReadonlyMap<string, number>;
  minLength: number;
}

/** The lists as rank lookups. Build once, when the lists arrive. */
export interface PasswordDictionaries {
  readonly entries: readonly Dictionary[];
  /** How many entries each list holds, for the page to quote. */
  readonly sizes: Readonly<Record<DictionaryName, number>>;
  /** log₁₀ P(next letter | letter), from `trainLetterModel`. */
  readonly letterModel: readonly number[];
}

/*
 * A first-order (letter-pair) model of English spelling, for letters no list explains.
 *
 * Password crackers that run out of dictionary words enumerate strings in order of
 * probability under a Markov model of letters (John the Ripper's Markov mode, OMEN). An
 * attacker going in that order has tried every string at least as likely as w after at
 * most 1 / P(w) guesses — so 1 / P(w) is a fair price for a run of letters that reads
 * like English, such as a word missing from the lists or a misspelt one, while a run of
 * unlikely pairs ("qzxv") costs more than random letters and stays priced as random.
 * Trained on the word and name lists, with add-one smoothing; index 26 is the word
 * boundary.
 */
const BOUNDARY = 26;
const MODEL_WIDTH = 27;
const LOWER_A_CODE = 97;

function modelIndex(char: string): number {
  const index = char.charCodeAt(0) - LOWER_A_CODE;
  return char.length === 1 && index >= 0 && index < BOUNDARY ? index : -1;
}

export function trainLetterModel(words: readonly string[]): number[] {
  const counts: number[] = Array.from(
    { length: MODEL_WIDTH * MODEL_WIDTH },
    () => 1
  );
  for (const word of words) {
    const indexes = [...word].map(modelIndex);
    if (indexes.some((index) => index < 0)) {
      continue;
    }
    let previous = BOUNDARY;
    for (const index of [...indexes, BOUNDARY]) {
      counts[previous * MODEL_WIDTH + index] += 1;
      previous = index;
    }
  }
  const model: number[] = [];
  for (let row = 0; row < MODEL_WIDTH; row += 1) {
    const cells = counts.slice(row * MODEL_WIDTH, (row + 1) * MODEL_WIDTH);
    const total = cells.reduce((sum, count) => sum + count, 0);
    model.push(...cells.map((count) => Math.log10(count / total)));
  }
  return model;
}

/** −log₁₀ P(word) under the model, word boundaries included; null unless a–z only. */
export function letterModelCost(
  word: string,
  model: readonly number[]
): number | null {
  let cost = 0;
  let previous = BOUNDARY;
  for (const char of word) {
    const index = modelIndex(char);
    if (index < 0) {
      return null;
    }
    cost -= model[previous * MODEL_WIDTH + index];
    previous = index;
  }
  return cost - model[previous * MODEL_WIDTH + BOUNDARY];
}

function rankMap(words: readonly string[]): Map<string, number> {
  const ranks = new Map<string, number>();
  for (const [index, word] of words.entries()) {
    if (!ranks.has(word)) {
      ranks.set(word, index + 1);
    }
  }
  return ranks;
}

/**
 * Short words count: "my", "dog" and "rex" are how people build passwords. Random
 * characters spell a short word now and then, but the cover search charges for every
 * extra piece (`MIN_GUESSES_BEFORE_GROWING_SEQUENCE`), so a stray match only wins
 * when it really is cheaper.
 */
export function buildDictionaries(lists: PasswordLists): PasswordDictionaries {
  const entries: Dictionary[] = [
    { name: "passwords", ranks: rankMap(lists.passwords), minLength: 3 },
    { name: "english", ranks: rankMap(lists.english), minLength: 2 },
    { name: "names", ranks: rankMap(lists.firstNames), minLength: 3 },
    { name: "surnames", ranks: rankMap(lists.surnames), minLength: 3 },
  ];
  return {
    entries,
    sizes: {
      passwords: lists.passwords.length,
      english: lists.english.length,
      names: lists.firstNames.length,
      surnames: lists.surnames.length,
    },
    letterModel: trainLetterModel([
      ...lists.english,
      ...lists.firstNames,
      ...lists.surnames,
    ]),
  };
}

/** Characters people swap in for letters, and the letters they stand for. */
const L33T_TABLE: Readonly<Record<string, readonly string[]>> = {
  "4": ["a"],
  "@": ["a"],
  "8": ["b"],
  "(": ["c"],
  "{": ["c"],
  "[": ["c"],
  "<": ["c"],
  "3": ["e"],
  "6": ["g"],
  "9": ["g"],
  "1": ["i", "l"],
  "!": ["i"],
  "|": ["i", "l"],
  "7": ["l", "t"],
  "0": ["o"],
  $: ["s"],
  "5": ["s"],
  "+": ["t"],
  "%": ["x"],
  "2": ["z"],
};

const MAX_L33T_VARIANTS = 32;
/** NIST SP 800-63B-4 asks services to accept at least 64 characters; we read that many. */
export const MAX_LENGTH = 64;
const MIN_WORD_LENGTH = 2;
const MIN_VARIANT_LENGTH = 4;

/* --------------------------------------------------------------- patterns */

export type PatternKind =
  | "dictionary"
  | "wordlike"
  | "sequence"
  | "repeat"
  | "keyboard"
  | "year"
  | "date"
  | "random";

export interface PatternMatch {
  kind: PatternKind;
  /** First and last character covered, inclusive (code points). */
  i: number;
  j: number;
  token: string;
  guesses: number;
  dictionary?: DictionaryName;
  /** The dictionary word, in lower case, after undoing l33t and reversal. */
  word?: string;
  rank?: number;
  l33t?: boolean;
  reversed?: boolean;
  /** Capitals beyond an all-lower-case word. */
  capitals?: boolean;
  repeatUnit?: string;
  repeatCount?: number;
  descending?: boolean;
  turns?: number;
  year?: number;
}

const ALL_LOWER = /^[^A-Z]+$/;
const ALL_UPPER = /^[^a-z]+$/;
const START_UPPER = /^[A-Z][^A-Z]+$/;
const END_UPPER = /^[^A-Z]+[A-Z]$/;
const UPPER = /[A-Z]/g;
const LOWER = /[a-z]/g;
const DIGIT = /^\d$/;
const LOWER_LETTER = /^[a-z]$/;
const UPPER_LETTER = /^[A-Z]$/;
const ASCII_SYMBOL = /^[\x20-\x2f\x3a-\x40\x5b-\x60\x7b-\x7e]$/;
const ALL_DIGITS = /^\d+$/;
const RECENT_CENTURY_YEAR = /^(19|20)\d\d$/;

export function binomial(n: number, k: number): number {
  if (k < 0 || k > n) {
    return 0;
  }
  let result = 1;
  for (let i = 1; i <= k; i += 1) {
    result = (result * (n - k + i)) / i;
  }
  return result;
}

/** zxcvbn's count of the ways a word's capitals could have been placed. */
export function uppercaseVariations(token: string): number {
  if (ALL_LOWER.test(token)) {
    return 1;
  }
  if (
    START_UPPER.test(token) ||
    END_UPPER.test(token) ||
    ALL_UPPER.test(token)
  ) {
    return 2;
  }
  const upper = token.match(UPPER)?.length ?? 0;
  const lower = token.match(LOWER)?.length ?? 0;
  let variations = 0;
  for (let i = 1; i <= Math.min(upper, lower); i += 1) {
    variations += binomial(upper + lower, i);
  }
  return Math.max(variations, 1);
}

/** zxcvbn's count of the ways a l33t spelling could have been chosen. */
export function l33tVariations(token: string, word: string): number {
  const lower = token.toLowerCase();
  let variations = 1;
  const pairs = new Set<string>();
  for (const [index, char] of [...lower].entries()) {
    if (char !== word.charAt(index)) {
      pairs.add(`${char}${word.charAt(index)}`);
    }
  }
  for (const pair of pairs) {
    const [subbed, letter] = [...pair];
    const s = [...lower].filter((char) => char === subbed).length;
    const u = [...lower].filter((char) => char === letter).length;
    if (s === 0 || u === 0) {
      variations *= 2;
    } else {
      let possibilities = 0;
      for (let i = 1; i <= Math.min(s, u); i += 1) {
        possibilities += binomial(s + u, i);
      }
      variations *= possibilities;
    }
  }
  return variations;
}

/** Every reading of a token with its l33t characters turned back into letters. */
export function unl33t(token: string): string[] {
  let variants = [""];
  for (const char of token.toLowerCase()) {
    const options = L33T_TABLE[char];
    if (options) {
      variants = variants
        .flatMap((variant) =>
          [...options, char].map((option) => variant + option)
        )
        .slice(0, MAX_L33T_VARIANTS);
    } else {
      variants = variants.map((variant) => variant + char);
    }
  }
  return variants.filter((variant) => variant !== token.toLowerCase());
}

function lookup(
  word: string,
  dictionaries: PasswordDictionaries
): { name: DictionaryName; rank: number } | null {
  let best: { name: DictionaryName; rank: number } | null = null;
  for (const dictionary of dictionaries.entries) {
    if (word.length < dictionary.minLength) {
      continue;
    }
    const rank = dictionary.ranks.get(word);
    if (rank !== undefined && (best === null || rank < best.rank)) {
      best = { name: dictionary.name, rank };
    }
  }
  return best;
}

function dictionaryMatch(
  chars: readonly string[],
  i: number,
  j: number,
  dictionaries: PasswordDictionaries
): PatternMatch | null {
  const token = chars.slice(i, j + 1).join("");
  const lower = token.toLowerCase();
  // Reversed and l33t readings start at four characters: below that they mostly find
  // accidents ("ta" as "at" backwards) rather than anything a person chose.
  const variants = lower.length >= MIN_VARIANT_LENGTH;
  const candidates: { word: string; l33t: boolean; reversed: boolean }[] = [
    { word: lower, l33t: false, reversed: false },
    ...(variants
      ? [
          { word: [...lower].reverse().join(""), l33t: false, reversed: true },
          ...unl33t(token).map((word) => ({
            word,
            l33t: true,
            reversed: false,
          })),
        ]
      : []),
  ];
  let best: PatternMatch | null = null;
  for (const candidate of candidates) {
    const found = lookup(candidate.word, dictionaries);
    if (found === null) {
      continue;
    }
    const guesses =
      found.rank *
      uppercaseVariations(token) *
      (candidate.l33t ? l33tVariations(token, candidate.word) : 1) *
      (candidate.reversed ? 2 : 1);
    if (best === null || guesses < best.guesses) {
      best = {
        kind: "dictionary",
        i,
        j,
        token,
        guesses,
        dictionary: found.name,
        word: candidate.word,
        rank: found.rank,
        l33t: candidate.l33t,
        reversed: candidate.reversed,
        capitals: !ALL_LOWER.test(token),
      };
    }
  }
  return best;
}

function dictionaryMatches(
  chars: readonly string[],
  dictionaries: PasswordDictionaries
): PatternMatch[] {
  const matches: PatternMatch[] = [];
  for (let i = 0; i < chars.length; i += 1) {
    for (let j = i + MIN_WORD_LENGTH - 1; j < chars.length; j += 1) {
      const match = dictionaryMatch(chars, i, j, dictionaries);
      if (match) {
        matches.push(match);
      }
    }
  }
  return matches;
}

/** Letter runs shorter than this are left to the dictionaries and to random pricing. */
const MIN_WORDLIKE_LENGTH = 4;

/** The cheapest letter-model reading of a stretch: as typed, or with l33t undone. */
function wordlikeMatch(
  chars: readonly string[],
  i: number,
  j: number,
  model: readonly number[]
): PatternMatch | null {
  const token = chars.slice(i, j + 1).join("");
  const lower = token.toLowerCase();
  const readings = [
    { word: lower, l33t: false },
    ...unl33t(token).map((word) => ({ word, l33t: true })),
  ];
  let best: PatternMatch | null = null;
  for (const reading of readings) {
    const cost = letterModelCost(reading.word, model);
    if (cost === null) {
      continue;
    }
    const guesses =
      10 ** cost *
      uppercaseVariations(token) *
      (reading.l33t ? l33tVariations(token, reading.word) : 1);
    if (best === null || guesses < best.guesses) {
      best = {
        kind: "wordlike",
        i,
        j,
        token,
        guesses,
        word: reading.word,
        l33t: reading.l33t,
        capitals: !ALL_LOWER.test(token),
      };
    }
  }
  return best;
}

function wordlikeMatches(
  chars: readonly string[],
  model: readonly number[]
): PatternMatch[] {
  const matches: PatternMatch[] = [];
  for (let i = 0; i < chars.length; i += 1) {
    for (let j = i + MIN_WORDLIKE_LENGTH - 1; j < chars.length; j += 1) {
      const match = wordlikeMatch(chars, i, j, model);
      if (match) {
        matches.push(match);
      }
    }
  }
  return matches;
}

/* ---------------------------------------------------------------- sequences */

function charClass(char: string): string | null {
  if (LOWER_LETTER.test(char)) {
    return "lower";
  }
  if (UPPER_LETTER.test(char)) {
    return "upper";
  }
  return DIGIT.test(char) ? "digit" : null;
}

function sequenceGuesses(token: string, descending: boolean): number {
  const first = token.charAt(0);
  let base = 26;
  if ("aAzZ019".includes(first)) {
    base = 4;
  } else if (DIGIT.test(first)) {
    base = 10;
  }
  return base * token.length * (descending ? 2 : 1);
}

/** Runs like abc, 2468, ZYX: one character class, one step of 1–3 each time. */
function sequenceMatches(chars: readonly string[]): PatternMatch[] {
  const matches: PatternMatch[] = [];
  let start = 0;
  while (start < chars.length - 2) {
    const kind = charClass(chars[start]);
    const delta = chars[start + 1].charCodeAt(0) - chars[start].charCodeAt(0);
    let end = start + 1;
    const usable =
      kind !== null &&
      delta !== 0 &&
      Math.abs(delta) <= 3 &&
      charClass(chars[end]) === kind;
    while (
      usable &&
      end + 1 < chars.length &&
      charClass(chars[end + 1]) === kind &&
      chars[end + 1].charCodeAt(0) - chars[end].charCodeAt(0) === delta
    ) {
      end += 1;
    }
    if (usable && end - start >= 2) {
      const token = chars.slice(start, end + 1).join("");
      matches.push({
        kind: "sequence",
        i: start,
        j: end,
        token,
        guesses: sequenceGuesses(token, delta < 0),
        descending: delta < 0,
      });
      start = end;
    } else {
      start += 1;
    }
  }
  return matches;
}

/* ------------------------------------------------------------------ repeats */

function repeatAt(
  chars: readonly string[],
  start: number
): { unit: string; count: number } | null {
  let best: { unit: string; count: number; covered: number } | null = null;
  for (let size = 1; size <= (chars.length - start) / 2; size += 1) {
    const unit = chars.slice(start, start + size).join("");
    let count = 1;
    while (
      chars.slice(start + count * size, start + (count + 1) * size).join("") ===
      unit
    ) {
      count += 1;
    }
    const covered = count * size;
    const long = size === 1 ? count >= 3 : count >= 2;
    if (long && (best === null || covered > best.covered)) {
      best = { unit, count, covered };
    }
  }
  return best ? { unit: best.unit, count: best.count } : null;
}

function repeatMatches(
  chars: readonly string[],
  context: EstimateContext
): PatternMatch[] {
  const matches: PatternMatch[] = [];
  let start = 0;
  while (start < chars.length) {
    const found = repeatAt(chars, start);
    if (found === null) {
      start += 1;
      continue;
    }
    const end = start + found.unit.length * found.count - 1;
    matches.push({
      kind: "repeat",
      i: start,
      j: end,
      token: chars.slice(start, end + 1).join(""),
      guesses: estimate(found.unit, context).guesses * found.count,
      repeatUnit: found.unit,
      repeatCount: found.count,
    });
    start = end + 1;
  }
  return matches;
}

/* ----------------------------------------------------------- keyboard walks */

/** US QWERTY rows with each row's offset from the left edge, in key widths. */
const KEYBOARD_ROWS: readonly {
  keys: string;
  shifted: string;
  offset: number;
}[] = [
  { keys: "`1234567890-=", shifted: "~!@#$%^&*()_+", offset: 0 },
  { keys: "qwertyuiop[]\\", shifted: "QWERTYUIOP{}|", offset: 1.5 },
  { keys: "asdfghjkl;'", shifted: 'ASDFGHJKL:"', offset: 1.75 },
  { keys: "zxcvbnm,./", shifted: "ZXCVBNM<>?", offset: 2.25 },
];

interface KeyPosition {
  x: number;
  y: number;
  shifted: boolean;
}

function keyPositions(): Map<string, KeyPosition> {
  const positions = new Map<string, KeyPosition>();
  for (const [y, row] of KEYBOARD_ROWS.entries()) {
    for (const [index, key] of [...row.keys].entries()) {
      positions.set(key, { x: row.offset + index, y, shifted: false });
      positions.set(row.shifted.charAt(index), {
        x: row.offset + index,
        y,
        shifted: true,
      });
    }
  }
  return positions;
}

const KEY_POSITIONS = keyPositions();

/** Neighbouring keys: beside each other, or in the next row less than a key apart. */
function step(a: KeyPosition, b: KeyPosition): string | null {
  const dy = b.y - a.y;
  const dx = b.x - a.x;
  const sameRow = dy === 0 && Math.abs(dx) === 1;
  const nextRow = Math.abs(dy) === 1 && Math.abs(dx) < 1;
  return sameRow || nextRow ? `${dx},${dy}` : null;
}

function averageDegree(): number {
  const keys = KEYBOARD_ROWS.flatMap((row) => [...row.keys]);
  let edges = 0;
  for (const a of keys) {
    for (const b of keys) {
      const from = KEY_POSITIONS.get(a);
      const to = KEY_POSITIONS.get(b);
      if (from && to && step(from, to)) {
        edges += 1;
      }
    }
  }
  return edges / keys.length;
}

const KEYBOARD_START_POSITIONS = KEY_POSITIONS.size;
const KEYBOARD_AVERAGE_DEGREE = averageDegree();

function keyboardGuesses(
  length: number,
  turns: number,
  shifted: number
): number {
  let guesses = 0;
  for (let i = 2; i <= length; i += 1) {
    for (let j = 1; j <= Math.min(turns, i - 1); j += 1) {
      guesses +=
        binomial(i - 1, j - 1) *
        KEYBOARD_START_POSITIONS *
        KEYBOARD_AVERAGE_DEGREE ** j;
    }
  }
  const unshifted = length - shifted;
  if (shifted === 0 || unshifted === 0) {
    return guesses * (shifted === 0 ? 1 : 2);
  }
  let variations = 0;
  for (let i = 1; i <= Math.min(shifted, unshifted); i += 1) {
    variations += binomial(length, i);
  }
  return guesses * variations;
}

function walkFrom(
  chars: readonly string[],
  start: number
): { end: number; turns: number; shifted: number } {
  let end = start;
  let turns = 0;
  let direction: string | null = null;
  let shifted = KEY_POSITIONS.get(chars[start])?.shifted ? 1 : 0;
  while (end + 1 < chars.length) {
    const from = KEY_POSITIONS.get(chars[end]);
    const to = KEY_POSITIONS.get(chars[end + 1]);
    const move = from && to ? step(from, to) : null;
    if (move === null || !to) {
      break;
    }
    if (move !== direction) {
      turns += 1;
      direction = move;
    }
    shifted += to.shifted ? 1 : 0;
    end += 1;
  }
  return { end, turns, shifted };
}

/** Runs of three or more neighbouring keys: qwerty, asdf, 1qaz2wsx in pieces. */
function keyboardMatches(chars: readonly string[]): PatternMatch[] {
  const matches: PatternMatch[] = [];
  let start = 0;
  while (start < chars.length) {
    const walk = walkFrom(chars, start);
    if (walk.end - start >= 2) {
      matches.push({
        kind: "keyboard",
        i: start,
        j: walk.end,
        token: chars.slice(start, walk.end + 1).join(""),
        guesses: keyboardGuesses(
          walk.end - start + 1,
          walk.turns,
          walk.shifted
        ),
        turns: walk.turns,
      });
      start = walk.end;
    } else {
      start += 1;
    }
  }
  return matches;
}

/* ------------------------------------------------------------ years, dates */

const MIN_YEAR_SPACE = 20;
const DATE_WITH_SEPARATOR = /^(\d{1,4})([\s/\\_.-])(\d{1,2})\2(\d{1,4})$/;

function yearSpace(year: number, referenceYear: number): number {
  return Math.max(Math.abs(year - referenceYear), MIN_YEAR_SPACE);
}

function fullYear(value: number, digits: number): number {
  if (digits > 2) {
    return value;
  }
  return value > 50 ? 1900 + value : 2000 + value;
}

function plausibleDate(day: number, month: number, year: number): boolean {
  return (
    day >= 1 &&
    day <= 31 &&
    month >= 1 &&
    month <= 12 &&
    year >= 1900 &&
    year <= 2099
  );
}

/**
 * The year of the most guessable valid reading — year first (y-m-d), or day and month
 * first in either order (d-m-y, m-d-y) — or null when no reading is a real date.
 */
function dateYear(
  parts: readonly string[],
  referenceYear: number
): number | null {
  const [a, b, c] = parts.map((part) => Number.parseInt(part, 10));
  const readings: [number, number, number][] = [];
  if (parts[0].length === 2 || parts[0].length === 4) {
    readings.push([c, b, fullYear(a, parts[0].length)]);
  }
  if (parts[2].length === 2 || parts[2].length === 4) {
    const year = fullYear(c, parts[2].length);
    readings.push([a, b, year], [b, a, year]);
  }
  const years = readings
    .filter(([day, month, year]) => plausibleDate(day, month, year))
    .map(([, , year]) => year)
    .sort(
      (left, right) =>
        yearSpace(left, referenceYear) - yearSpace(right, referenceYear)
    );
  return years.at(0) ?? null;
}

/** Where to cut 6 and 8 digits into day, month and year (ddmmyy, yyyymmdd…). */
const UNSEPARATED_SPLITS: Readonly<
  Record<number, readonly (readonly [number, number])[]>
> = {
  6: [[2, 4]],
  8: [
    [2, 4],
    [4, 6],
  ],
};

function unseparatedDateYear(
  digits: string,
  referenceYear: number
): number | null {
  for (const [first, second] of UNSEPARATED_SPLITS[digits.length] ?? []) {
    const year = dateYear(
      [
        digits.slice(0, first),
        digits.slice(first, second),
        digits.slice(second),
      ],
      referenceYear
    );
    if (year !== null) {
      return year;
    }
  }
  return null;
}

function dateMatches(
  chars: readonly string[],
  referenceYear: number
): PatternMatch[] {
  const matches: PatternMatch[] = [];
  for (let i = 0; i < chars.length; i += 1) {
    for (let j = i + 3; j < Math.min(chars.length, i + 10); j += 1) {
      const token = chars.slice(i, j + 1).join("");
      const separated = DATE_WITH_SEPARATOR.exec(token);
      let year: number | null = null;
      if (separated) {
        year = dateYear(
          [separated[1], separated[3], separated[4]],
          referenceYear
        );
      } else if (ALL_DIGITS.test(token)) {
        year = unseparatedDateYear(token, referenceYear);
      }
      if (year !== null) {
        matches.push({
          kind: "date",
          i,
          j,
          token,
          year,
          guesses: 365 * yearSpace(year, referenceYear) * (separated ? 4 : 1),
        });
      }
    }
  }
  return matches;
}

function yearMatches(
  chars: readonly string[],
  referenceYear: number
): PatternMatch[] {
  const matches: PatternMatch[] = [];
  for (let i = 0; i + 3 < chars.length; i += 1) {
    const token = chars.slice(i, i + 4).join("");
    const year = Number.parseInt(token, 10);
    if (RECENT_CENTURY_YEAR.test(token)) {
      matches.push({
        kind: "year",
        i,
        j: i + 3,
        token,
        year,
        guesses: yearSpace(year, referenceYear),
      });
    }
  }
  return matches;
}

/* -------------------------------------------------------- the cheapest cover */

export interface CharacterPools {
  lower: boolean;
  upper: boolean;
  digits: boolean;
  symbols: boolean;
  /** Letters outside ASCII, emoji and the rest. */
  other: boolean;
  /** 26 + 26 + 10 + 33 + 100 for the pools present. */
  size: number;
}

const POOL_SIZES = {
  lower: 26,
  upper: 26,
  digits: 10,
  symbols: 33,
  other: 100,
};

export function characterPools(text: string): CharacterPools {
  const pools = {
    lower: false,
    upper: false,
    digits: false,
    symbols: false,
    other: false,
  };
  for (const char of text) {
    if (LOWER_LETTER.test(char)) {
      pools.lower = true;
    } else if (UPPER_LETTER.test(char)) {
      pools.upper = true;
    } else if (DIGIT.test(char)) {
      pools.digits = true;
    } else if (ASCII_SYMBOL.test(char)) {
      pools.symbols = true;
    } else {
      pools.other = true;
    }
  }
  const size = (Object.keys(POOL_SIZES) as (keyof typeof POOL_SIZES)[]).reduce(
    (sum, key) => sum + (pools[key] ? POOL_SIZES[key] : 0),
    0
  );
  return { ...pools, size };
}

const MIN_GUESSES_BEFORE_GROWING_SEQUENCE = 10_000;
const MIN_SUBMATCH_GUESSES_SINGLE = 10;
const MIN_SUBMATCH_GUESSES_MULTI = 50;

function log10Factorial(n: number): number {
  let sum = 0;
  for (let i = 2; i <= n; i += 1) {
    sum += Math.log10(i);
  }
  return sum;
}

/** log10(10^a + 10^b) without overflow. */
function log10Sum(a: number, b: number): number {
  const high = Math.max(a, b);
  return high + Math.log10(10 ** (a - high) + 10 ** (b - high));
}

/** The cheapest way found so far to cover a prefix with a given number of segments. */
interface CoverPrefix {
  /** log10 of the product of the segments' guesses. */
  logProduct: number;
  last: PatternMatch;
  previous: CoverPrefix | null;
}

function priced(match: PatternMatch, length: number): number {
  if (match.j - match.i + 1 === length) {
    return Math.max(match.guesses, 1);
  }
  const floor =
    match.j === match.i
      ? MIN_SUBMATCH_GUESSES_SINGLE
      : MIN_SUBMATCH_GUESSES_MULTI;
  return Math.max(match.guesses, floor);
}

function extend(
  best: Map<number, CoverPrefix>[],
  match: PatternMatch,
  length: number
): void {
  const cost = Math.log10(priced(match, length));
  const before: [number, CoverPrefix | null][] =
    match.i === 0 ? [[0, null]] : [...best[match.i - 1]];
  for (const [count, prefix] of before) {
    // Two random runs side by side are one longer random run, priced once.
    if (match.kind === "random" && prefix?.last.kind === "random") {
      continue;
    }
    const logProduct = (prefix?.logProduct ?? 0) + cost;
    const current = best[match.j].get(count + 1);
    if (current === undefined || logProduct < current.logProduct) {
      best[match.j].set(count + 1, {
        logProduct,
        last: match,
        previous: prefix,
      });
    }
  }
}

function unwind(prefix: CoverPrefix): PatternMatch[] {
  const sequence: PatternMatch[] = [];
  for (let node: CoverPrefix | null = prefix; node; node = node.previous) {
    sequence.unshift(node.last);
  }
  return sequence;
}

/**
 * Characters no pattern explains, priced as random picks from the pool of the WHOLE
 * password — an attacker does not know which stretch uses which characters, and pricing
 * each stretch by its own pool would reward cutting the password into pieces.
 */
function randomMatch(
  chars: readonly string[],
  i: number,
  j: number,
  poolSize: number
): PatternMatch {
  const token = chars.slice(i, j + 1).join("");
  return { kind: "random", i, j, token, guesses: poolSize ** (j - i + 1) };
}

/**
 * The decomposition with the fewest total guesses — zxcvbn's search. A cover of l
 * segments costs l! × (product of the segments' guesses) + 10000^(l − 1): the factorial
 * for the order the attacker must also guess, the second term so that splitting a
 * password into more pieces is never free.
 */
function cheapestCover(
  chars: readonly string[],
  matches: readonly PatternMatch[]
): { log10Guesses: number; sequence: PatternMatch[] } {
  const length = chars.length;
  const poolSize = Math.max(characterPools(chars.join("")).size, 1);
  const best: Map<number, CoverPrefix>[] = Array.from(
    { length },
    () => new Map()
  );
  for (let k = 0; k < length; k += 1) {
    for (const match of matches) {
      if (match.j === k) {
        extend(best, match, length);
      }
    }
    for (let i = 0; i <= k; i += 1) {
      extend(best, randomMatch(chars, i, k, poolSize), length);
    }
  }
  let bestTotal = Number.POSITIVE_INFINITY;
  let bestPrefix: CoverPrefix | null = null;
  for (const [count, prefix] of best[length - 1]) {
    const total = log10Sum(
      log10Factorial(count) + prefix.logProduct,
      (count - 1) * Math.log10(MIN_GUESSES_BEFORE_GROWING_SEQUENCE)
    );
    if (total < bestTotal) {
      bestTotal = total;
      bestPrefix = prefix;
    }
  }
  return {
    log10Guesses: bestTotal,
    sequence: bestPrefix ? unwind(bestPrefix) : [],
  };
}

/* ------------------------------------------------------------------ result */

export interface StrengthEstimate {
  /** Characters (code points) considered — the first 64. */
  length: number;
  truncated: boolean;
  pools: CharacterPools;
  /** length × log₂(pool size). */
  poolBits: number;
  guesses: number;
  log10Guesses: number;
  /** log₂ of the estimated guesses. */
  bits: number;
  sequence: PatternMatch[];
  /** Seconds to make that many guesses, per attack. */
  seconds: Record<AttackId, number>;
  /** 0–4: how many of the four attacks it would hold out against for a year. */
  rating: number;
}

/** What every part of the estimate needs: the year to date against, and the lists. */
interface EstimateContext {
  referenceYear: number;
  dictionaries: PasswordDictionaries;
}

/** The core estimate, used recursively for the unit of a repeat. */
function estimate(
  password: string,
  context: EstimateContext
): { guesses: number; log10Guesses: number; sequence: PatternMatch[] } {
  const chars = [...password].slice(0, MAX_LENGTH);
  if (chars.length === 0) {
    return { guesses: 1, log10Guesses: 0, sequence: [] };
  }
  const { referenceYear, dictionaries } = context;
  const matches = [
    ...dictionaryMatches(chars, dictionaries),
    ...wordlikeMatches(chars, dictionaries.letterModel),
    ...sequenceMatches(chars),
    ...repeatMatches(chars, context),
    ...keyboardMatches(chars),
    ...dateMatches(chars, referenceYear),
    ...yearMatches(chars, referenceYear),
  ];
  const { log10Guesses, sequence } = cheapestCover(chars, matches);
  return { guesses: 10 ** log10Guesses, log10Guesses, sequence };
}

/**
 * Estimate a password's strength. `referenceYear` is the current year — years and dates
 * close to it are the likeliest guesses — and `dictionaries` come from
 * `buildDictionaries`.
 */
export function estimateStrength(
  password: string,
  referenceYear: number,
  dictionaries: PasswordDictionaries
): StrengthEstimate {
  const chars = [...password];
  const considered = chars.slice(0, MAX_LENGTH);
  const pools = characterPools(considered.join(""));
  const { guesses, log10Guesses, sequence } = estimate(considered.join(""), {
    referenceYear,
    dictionaries,
  });
  const seconds = Object.fromEntries(
    ATTACKS.map((attack) => [attack.id, guesses / attack.perSecond])
  ) as Record<AttackId, number>;
  const rating = ATTACKS.filter(
    (attack) => guesses / attack.perSecond >= SECONDS_PER_YEAR
  ).length;
  return {
    length: considered.length,
    truncated: chars.length > MAX_LENGTH,
    pools,
    poolBits: pools.size > 0 ? considered.length * Math.log2(pools.size) : 0,
    guesses,
    log10Guesses,
    bits: log10Guesses / Math.log10(2),
    sequence,
    seconds,
    rating: considered.length === 0 ? 0 : rating,
  };
}

/* -------------------------------------------------------------- reporting */

export type DurationUnit =
  | "second"
  | "minute"
  | "hour"
  | "day"
  | "month"
  | "year";

/** A time in the largest whole unit that fits, for `Intl.NumberFormat`'s unit style. */
export type CrackTime =
  | { kind: "instant" }
  | { kind: "span"; unit: DurationUnit; value: number }
  | { kind: "eons" };

const DURATION_STEPS: readonly { unit: DurationUnit; seconds: number }[] = [
  { unit: "year", seconds: SECONDS_PER_YEAR },
  { unit: "month", seconds: SECONDS_PER_YEAR / 12 },
  { unit: "day", seconds: 86_400 },
  { unit: "hour", seconds: 3600 },
  { unit: "minute", seconds: 60 },
  { unit: "second", seconds: 1 },
];

/** Beyond this many years the number means nothing to a reader. */
const EONS_YEARS = 1e9;

export function crackTime(seconds: number): CrackTime {
  if (!(seconds >= 1)) {
    return { kind: "instant" };
  }
  if (seconds / SECONDS_PER_YEAR >= EONS_YEARS) {
    return { kind: "eons" };
  }
  // `seconds >= 1` here, so the last step always matches.
  const step = DURATION_STEPS.find(
    (candidate) => seconds >= candidate.seconds
  ) ?? { unit: "second", seconds: 1 };
  return { kind: "span", unit: step.unit, value: seconds / step.seconds };
}

export type SuggestionId =
  | "common"
  | "commonPart"
  | "words"
  | "l33t"
  | "capitals"
  | "reversed"
  | "sequence"
  | "repeat"
  | "keyboard"
  | "date"
  | "length"
  | "good";

/** NIST SP 800-63B-4's minimum for a password used as the only factor. */
export const NIST_MIN_LENGTH = 15;

function commonOrWords(match: PatternMatch, length: number): SuggestionId {
  if (match.dictionary !== "passwords") {
    return "words";
  }
  return match.j - match.i + 1 === length ? "common" : "commonPart";
}

function matchSuggestions(match: PatternMatch, length: number): SuggestionId[] {
  if (match.kind === "dictionary") {
    return [
      commonOrWords(match, length),
      ...(match.l33t ? (["l33t"] as const) : []),
      ...(match.capitals ? (["capitals"] as const) : []),
      ...(match.reversed ? (["reversed"] as const) : []),
    ];
  }
  if (match.kind === "wordlike") {
    return [
      "words",
      ...(match.l33t ? (["l33t"] as const) : []),
      ...(match.capitals ? (["capitals"] as const) : []),
    ];
  }
  if (match.kind === "year" || match.kind === "date") {
    return ["date"];
  }
  return match.kind === "random" ? [] : [match.kind];
}

/**
 * What to tell the reader, in a fixed order. A password that survives every attack in
 * `ATTACKS` gets only "good": a strong passphrase is made of words, and telling its
 * owner that words are guessable would be advice against the right thing.
 */
export function suggestionsFor(estimate: StrengthEstimate): SuggestionId[] {
  if (estimate.length === 0) {
    return [];
  }
  if (estimate.rating === ATTACKS.length) {
    return ["good"];
  }
  const found = new Set(
    estimate.sequence.flatMap((match) =>
      matchSuggestions(match, estimate.length)
    )
  );
  if (estimate.length < NIST_MIN_LENGTH) {
    found.add("length");
  }
  const order: readonly SuggestionId[] = [
    "common",
    "commonPart",
    "words",
    "l33t",
    "capitals",
    "reversed",
    "sequence",
    "repeat",
    "keyboard",
    "date",
    "length",
  ];
  const chosen = order.filter((id) => found.has(id));
  return chosen.length > 0 ? chosen : ["good"];
}
